import { QueryLoader } from "@Light/components/app/QueryLoader";
import { DetailLabel } from "@Light/components/page/detail/DetailLabel";
import { MutationButton } from "@Light/components/page/MutationButton";
import { PageBody } from "@Light/components/page/PageBody";
import { SuccessBanner } from "@Light/components/page/SuccessBanner";
import { Datepicker } from "@Light/components/system/Datepicker";
import { Field } from "@Light/components/system/Fieldset";
import { Input } from "@Light/components/system/Input";
import { RadioGroup } from "@Light/components/system/RadioGroup";
import type {
  PlanResponse,
  RequirementsPatch,
} from "@Light/services/lightTypes";
import { ServiceEvent } from "@Light/services/lightTypes";
import { EMAIL_PATTERN } from "@Light/utils/email";
import { HeartHand } from "@untitledui/icons";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { EnrollHelpButtonFooter } from "../EnrollHelpButtonFooter";
import { FieldLabel } from "../FieldLabel";
import { useEnrollment } from "../flow/context";
import { isUnsatisfied, maybeRequirementValue } from "../flow/screen";
import { useEnrollmentStepper } from "../flow/useEnrollmentStepper";
import { useTermRates } from "../rate";
import { EnrollRoute } from "../routes";
import { useEnrollmentUtilityConfiguration } from "../useEnrollmentUtilityConfiguration";

type IdentityForm = {
  first_name: string;
  last_name: string;
  email: string;
};

const serviceEventOptions = [
  {
    value: ServiceEvent.Switch,
    label: "Yes",
    message: "We'll handle the switch from your provider.",
  },
  {
    value: ServiceEvent.MoveIn,
    label: "No",
    message: "We'll set up service with your local utility.",
  },
] as const;

export const VerifyIdentity = () => {
  const { requirements } = useEnrollment();
  const { rates, query } = useTermRates();
  const rateId = maybeRequirementValue(requirements.acceptance)?.rate_id;
  const rate = rates.find((r) => r.rate?.uuid === rateId)?.rate;

  const isError = query.isError || (query.isSuccess && !rate);
  const isSuccess = query.isSuccess && !isError;
  return (
    <QueryLoader
      queries={[{ ...query, isError, isSuccess }]}
      errorMessage="Error loading your rate. Please try again later"
    >
      {rate && <VerifyIdentityB1 rate={rate} />}
    </QueryLoader>
  );
};

const VerifyIdentityB1 = ({ rate }: { rate: PlanResponse }) => {
  const { next, back, nextMutation, requirements } = useEnrollment();
  const { needsServiceEvent } = useEnrollmentUtilityConfiguration();
  const stepper = useEnrollmentStepper(EnrollRoute.VerifyIdentity);

  const nameOnFile = maybeRequirementValue(requirements.name);
  const emailOnFile = maybeRequirementValue(requirements.email)?.value;
  // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-assignment
  const initialServiceEvent = maybeRequirementValue(requirements.service_event)
    ?.value as ServiceEvent | undefined;
  const initialServiceStartDate = maybeRequirementValue(
    requirements.service_start_date,
  )?.value;

  const {
    register,
    handleSubmit,
    formState: { errors, dirtyFields },
  } = useForm<IdentityForm>({
    defaultValues: {
      first_name: nameOnFile?.first ?? "",
      last_name: nameOnFile?.last ?? "",
      email: emailOnFile ?? "",
    },
  });

  const [isShowingServiceEvent] = useState(
    () => needsServiceEvent || isUnsatisfied(requirements.service_event),
  );
  const [serviceEvent, setServiceEvent] = useState<ServiceEvent | undefined>(
    initialServiceEvent,
  );
  const serviceEventMessage = serviceEventOptions.find(
    (option) => option.value === serviceEvent,
  )?.message;

  const [serviceStartDate, setServiceStartDate] = useState<string>(
    initialServiceStartDate ?? rate.earliest_start_date,
  );

  const [isServiceEventError, setIsServiceEventError] = useState(false);

  const onSubmit = handleSubmit(
    async (data) => {
      if (isShowingServiceEvent && !serviceEvent) {
        setIsServiceEventError(true);
        return;
      }

      const patch: RequirementsPatch = {};

      if (dirtyFields.first_name || dirtyFields.last_name) {
        patch.name = { first: data.first_name, last: data.last_name };
      }

      if (dirtyFields.email) {
        patch.email = { value: data.email };
      }

      if (serviceEvent && serviceEvent !== initialServiceEvent) {
        patch.service_event = { value: serviceEvent };
      }

      if (serviceStartDate !== initialServiceStartDate) {
        patch.service_start_date = { value: serviceStartDate };
      }

      if (Object.keys(patch).length === 0) {
        await next();
        return;
      }

      await next(patch);
    },
    // The service event isn't a react-hook-form field, so it needs flagging
    // here too when the form's own fields fail validation.
    () => {
      setIsServiceEventError(isShowingServiceEvent && !serviceEvent);
    },
  );

  return (
    <form onSubmit={onSubmit}>
      <PageBody
        onBack={back}
        title="Let's set up your plan"
        stepper={stepper}
        footer={
          <EnrollHelpButtonFooter>
            <MutationButton
              size="lg"
              mutation={nextMutation}
              mutateButtonText="Save and continue"
              errorMessage="Failed submitting identity information. Please try again later"
              data-testid="verify-identity-submit"
            />
          </EnrollHelpButtonFooter>
        }
      >
        <div className="flex flex-col gap-6">
          <Field className="flex flex-col gap-1.5">
            <FieldLabel>What's your name?</FieldLabel>
            <div className="flex flex-row gap-3">
              <Input
                type="text"
                aria-label="First name"
                disabled={!!nameOnFile?.first}
                isError={!!errors.first_name}
                data-testid="first-name-input"
                {...register("first_name", { required: true })}
              />
              <Input
                type="text"
                aria-label="Last name"
                disabled={!!nameOnFile?.last}
                isError={!!errors.last_name}
                data-testid="last-name-input"
                {...register("last_name", { required: true })}
              />
            </div>
          </Field>

          <Field className="flex flex-col gap-1.5">
            <FieldLabel>What's your email?</FieldLabel>
            <div className="flex flex-row gap-3">
              <Input
                type="text"
                aria-label="Email"
                disabled={!!emailOnFile}
                isError={!!errors.email}
                data-testid="email-input"
                {...register("email", {
                  required: true,
                  pattern: EMAIL_PATTERN,
                })}
              />
            </div>
          </Field>

          <Field className="flex flex-col gap-1.5">
            <DetailLabel>When would you like to start service?</DetailLabel>
            <Datepicker
              selectedDate={serviceStartDate}
              onChange={setServiceStartDate}
              minSelectableDate={rate.earliest_start_date}
              maxSelectableDate={rate.latest_start_date}
              testId="service-start-date-picker"
            />
          </Field>

          {isShowingServiceEvent && (
            <div className="flex flex-col gap-4">
              <Field className="flex flex-col gap-1.5">
                <FieldLabel>
                  Do you already pay for electricity at this address?
                </FieldLabel>
                <RadioGroup
                  name="service-event"
                  orientation="horizontal"
                  options={serviceEventOptions.map((option) => ({
                    value: option.value,
                    label: option.label,
                    testId: `service-event-${option.value}`,
                  }))}
                  value={serviceEvent}
                  isError={isServiceEventError}
                  onChange={(value) => {
                    setServiceEvent(value);
                    setIsServiceEventError(false);
                  }}
                />
              </Field>

              {serviceEventMessage && (
                <SuccessBanner key={serviceEvent} icon={HeartHand}>
                  {serviceEventMessage}
                </SuccessBanner>
              )}
            </div>
          )}
        </div>
      </PageBody>
    </form>
  );
};
